import React from 'react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useCompanyInfo, usePageData } from '../lib/cms';

export default function ContactPage() {
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { page } = usePageData('kontakt');
  const heroBg = page.heroImage || '';

  return (
    <>
      <SEO
        title="Kontakt - CMSNord.dk"
        description="Kontakt CMSNord.dk på Sdr. Omfartsvej 322, 9700 Brønderslev. Tlf: 25 62 35 05 eller e-mail info@cmsnord.dk for uforpligtende tilbud på entreprenørarbejde og vognmandskørsel."
        canonicalUrl={`${window.location.origin}/kontakt`}
        ogImage={heroBg}
      />

      {/* Header Banner with high quality background image & dark overlay */}
      <section className="relative bg-[#121212] text-white py-16 sm:py-24 border-b border-zinc-800 overflow-hidden">
        {heroBg && (
          <div className="absolute inset-0 z-0">
            <img
              src={heroBg}
              alt="Kontakt CMSNord.dk"
              className="w-full h-full object-cover opacity-35 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/80 to-[#121212]/60" />
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#F4AF00] text-xs font-bold uppercase tracking-widest drop-shadow">
            Vi står klar til at hjælpe
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-white mt-1 mb-4 drop-shadow">
            Kontakt CMSNord.dk
          </h1>
          <p className="text-zinc-200 text-base max-w-2xl drop-shadow">
            Har du spørgsmål, eller ønsker du et uforpligtende tilbud? Ring direkte, send en e-mail eller benyt kontaktformularen nedenfor.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Contact Details & Map */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white border border-zinc-300 rounded-none p-6 sm:p-8 space-y-6 shadow-md border-t-4 border-t-[#F4AF00]">
                <h2 className="text-2xl font-black text-[#121212] uppercase tracking-tight border-b border-zinc-200 pb-3">
                  Kontaktoplysninger
                </h2>

                <div className="space-y-6 sm:space-y-4">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                    <div className="w-10 h-10 bg-[#F4AF00] text-[#121212] font-bold flex items-center justify-center text-lg flex-shrink-0">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase block">Virksomhedsnavn</span>
                      <p className="text-lg font-black text-[#121212]">{COMPANY_INFO.name}</p>
                      <p className="text-xs text-zinc-500">(Tidligere {COMPANY_INFO.previousName})</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                    <div className="w-10 h-10 bg-[#F4AF00] text-[#121212] font-bold flex items-center justify-center text-lg flex-shrink-0">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase block">Adresse</span>
                      <p className="text-base font-bold text-[#121212]">{COMPANY_INFO.address}</p>
                      <p className="text-sm text-zinc-700">{COMPANY_INFO.zipCity}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                    <div className="w-10 h-10 bg-[#F4AF00] text-[#121212] font-bold flex items-center justify-center text-lg flex-shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase block">Telefon</span>
                      <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="nowrap-contact text-xl font-black text-[#121212] hover:text-[#D99B00]">
                        {COMPANY_INFO.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                    <div className="w-10 h-10 bg-[#F4AF00] text-[#121212] font-bold flex items-center justify-center text-lg flex-shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase block">E-mail</span>
                      <a href={`mailto:${COMPANY_INFO.email}`} className="nowrap-contact text-sm font-bold text-[#121212] hover:text-[#D99B00] transition-colors break-all">
                        {COMPANY_INFO.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                    <div className="w-10 h-10 bg-[#F4AF00] text-[#121212] font-bold flex items-center justify-center text-lg flex-shrink-0">
                      <i className="fa-solid fa-id-card" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase block">CVR-nummer</span>
                      <p className="text-sm font-mono font-bold text-[#121212]">{COMPANY_INFO.cvr}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map embed */}
              <div className="bg-white border border-zinc-300 rounded-none overflow-hidden shadow-sm">
                <div className="p-4 bg-[#121212] border-b border-zinc-800">
                  <p className="text-xs text-[#F4AF00] font-bold uppercase tracking-wider">Find os her</p>
                  <p className="text-sm font-semibold text-white">{COMPANY_INFO.fullAddress}</p>
                </div>
                <div className="h-64 w-full">
                  <iframe
                    title="Kort over CMSNord.dk i Brønderslev"
                    src={COMPANY_INFO.locationMapUrl}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm title="Send os en besked eller tilbudsforespørgsel" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
